import axios from 'axios';
import { 
  AddDimensionTableRequest, 
  AddFactTableRequest, 
  AddHierarchyRequest,
  DimensionAttribute,
  Measure,
  ForeignKey
} from '../types/semantic';

// Use environment variable or fallback to production
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.shannontechnologies.io/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Add auth token to requests automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors more gracefully
api.interceptors.response.use(
  (response) => {
    // Unwrap the backend ApiResponse wrapper
    if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
      response.data = response.data.data;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_info');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Types
export interface User {
  id: string;
  username: string;
  email: string;
  role: 'admin' | 'data_engineer' | 'analyst' | 'demo' | 'sales_manager' | 'production_engineer' | 'production_manager';
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  role: User['role'];
}

// Types (matching your Rust models)
export interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  created_at: string;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  stock_quantity: number;
  created_at: string;
}

export interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface SalesOrder {
  id: string;
  customer_id: string;
  order_number: string;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  total_amount: number;
  order_date: string;
  items: OrderItem[];
  notes?: string;
}

export interface CreateCustomerRequest {
  name: string;
  email: string;
  phone?: string;
  address?: string;
}

export interface CreateOrderRequest {
  customer_id: string;
  items: { product_id: string; quantity: number }[];
  notes?: string;
}

export interface CreateProductRequest {
  name: string;
  description?: string;
  price: number;
  stock_quantity: number;
}

// API functions
export const customerApi = {
  getAll: () => api.get<Customer[]>('/customers'),
  getById: (id: string) => api.get<Customer>(`/customers/${id}`),
  create: (data: CreateCustomerRequest) => api.post<Customer>('/customers', data),
  update: (id: string, data: CreateCustomerRequest) => api.put<Customer>(`/customers/${id}`, data), // Add this line
};

export const productApi = {
  getAll: () => api.get<Product[]>('/products'),
  create: (data: CreateProductRequest) => api.post<Product>('/products', data),
  update: (id: string, data: CreateProductRequest) => api.put<Product>(`/products/${id}`, data), // ← Add this
};

export const orderApi = {
  getAll: () => api.get<SalesOrder[]>('/orders'),
  getById: (id: string) => api.get<SalesOrder>(`/orders/${id}`),
  create: (data: CreateOrderRequest) => api.post<SalesOrder>('/orders', data),
  updateStatus: (id: string, status: string) => api.put<SalesOrder>(`/orders/${id}`, { status }),
};

// Auth API
export const authApi = {
  login: (data: LoginRequest) => api.post<LoginResponse>('/auth/login', data),
  register: (data: RegisterRequest) => api.post<User>('/auth/register', data),
  me: () => api.get<User>('/auth/me'),
};

export interface AuditLog {
  id: string;
  user_id?: string;
  username?: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  details?: string;
  ip_address?: string;
  user_agent?: string;
  status_code: number;
  created_at: string;
}

export const auditApi = {
  getLogs: (limit?: number, offset?: number) =>
    api.get<AuditLog[]>(`/audit/logs?limit=${limit || 50}&offset=${offset || 0}`),
  getMyLogs: (limit?: number) =>
    api.get<AuditLog[]>(`/audit/my-logs?limit=${limit || 50}`),
};

export interface UserDetails {
  id: string;
  username: string;
  email: string;
  role: User['role'];
  is_active: boolean;
  created_at: string;
  last_login?: string | null;
}

export interface UserListResponse {
  users: UserDetails[];
  total: number;
  page: number;
  page_size: number;
}

export interface UserListParams {
  search?: string;
  /** Backend role slug, e.g. `admin` */
  role?: string;
  /** `active` | `inactive` | omit for all */
  status?: string;
  sort?: string;
  limit?: number;
  offset?: number;
}

export interface UpdateUserRequest {
  username: string;
  email: string;
  role: User['role'];
  is_active: boolean;
  /** Admin password reset — min 8 characters when set */
  password?: string;
}

function userListQuery(params: UserListParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};
  if (params.search != null && params.search !== '') q.search = params.search;
  if (params.role != null && params.role !== '' && params.role !== 'all') q.role = params.role;
  if (params.status != null && params.status !== '' && params.status !== 'all') q.status = params.status;
  if (params.sort != null && params.sort !== '') q.sort = params.sort;
  if (params.limit != null) q.limit = params.limit;
  if (params.offset != null) q.offset = params.offset;
  return q;
}

export const userAdminApi = {
  list: (params: UserListParams) =>
    api.get<UserListResponse>('/admin/users', { params: userListQuery(params) }),

  /** Same filters as list; server caps at 10k rows. */
  exportCsv: async (params: Omit<UserListParams, 'limit' | 'offset'>) => {
    const res = await api.get<Blob>('/admin/users/export', {
      params: userListQuery(params),
      responseType: 'blob',
    });
    const ct = String(res.headers['content-type'] ?? '');
    if (ct.includes('application/json')) {
      const text = await new Response(res.data).text();
      let msg = 'Export failed';
      try {
        const j = JSON.parse(text) as { error?: string };
        if (j.error) msg = j.error;
      } catch {
        if (text) msg = text.slice(0, 280);
      }
      throw new Error(msg);
    }
    return res.data;
  },

  create: (data: RegisterRequest) => api.post<UserDetails>('/admin/users', data),
  update: (id: string, data: UpdateUserRequest) => api.put<UserDetails>(`/admin/users/${id}`, data),
  delete: (id: string) => api.delete(`/admin/users/${id}`),
  toggleStatus: (id: string) => api.patch<UserDetails>(`/admin/users/${id}/toggle`),
};

export interface ImportResult {
  success_count: number;
  error_count: number;
  errors: ImportError[];
  imported_ids: string[];
}

export interface ImportError {
  row: number;
  field: string;
  message: string;
  data: any;
}

// Import/Export API functions
export const importExportApi = {
  // Import functions
  importCustomersCSV: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ImportResult>('/import/customers/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importProductsCSV: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ImportResult>('/import/products/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importOrdersCSV: (file: File) => { // ← Add this
    const formData = new FormData();
    formData.append('file', file);
    return api.post<ImportResult>('/import/orders/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Export functions
  exportCustomersCSV: () => api.get('/export/customers/csv', { responseType: 'blob' }),
  exportProductsCSV: () => api.get('/export/products/csv', { responseType: 'blob' }),
  exportOrdersCSV: () => api.get('/export/orders/csv', { responseType: 'blob' }), // ← Add this
  exportAllDataJSON: () => api.get('/export/all/json', { responseType: 'blob' }),
};

// Add these interfaces and API functions to your existing api.ts file

// Schema Management Types
export interface DatabaseSchema {
  tables: TableInfo[];
  relationships: Relationship[];
  indexes: IndexInfo[];
}

export interface TableInfo {
  name: string;
  schema: string;
  columns: ColumnInfo[];
  row_count?: number;
  size_bytes?: number;
  comment?: string;
}

export interface ColumnInfo {
  name: string;
  data_type: string;
  is_nullable: boolean;
  is_primary_key: boolean;
  is_foreign_key: boolean;
  default_value?: string;
  max_length?: number;
  comment?: string;
}

export interface Relationship {
  constraint_name: string;
  source_table: string;
  source_column: string;
  target_table: string;
  target_column: string;
  relationship_type: 'OneToOne' | 'OneToMany' | 'ManyToOne' | 'ManyToMany';
}

export interface IndexInfo {
  name: string;
  table_name: string;
  columns: string[];
  is_unique: boolean;
  is_primary: boolean;
  index_type: string;
}

export interface TableDetails {
  table_info: TableInfo;
  sample_data: Record<string, any>[];
}

export interface TableStatistics {
  table_name: string;
  size_pretty: string;
  size_bytes: number;
  column_statistics: ColumnStatistics[];
}

export interface ColumnStatistics {
  column_name: string;
  n_distinct?: number;
  correlation?: number;
}

export interface DatabaseStatistics {
  tables: TableStatistics[];
}

// Schema API functions - moved below to include database management functions

// Add these interfaces and API functions:

export interface BulkOperationResult {
  success_count: number;
  error_count: number;
  errors: BulkOperationError[];
  affected_ids: string[];
  rollback_data?: string;
}

export interface BulkOperationError {
  id: string;
  message: string;
  data: any;
}

export interface BulkUpdateCustomerRequest {
  ids: string[];
  updates: {
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
}

export interface BulkUpdateProductRequest {
  ids: string[];
  updates: {
    name?: string;
    description?: string;
    price?: number;
    stock_quantity?: number;
  };
}

export interface BulkUpdateOrderStatusRequest {
  ids: string[];
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
}

export interface BulkDeleteRequest {
  ids: string[];
}

// Bulk Operations API
export const bulkOperationsApi = {
  // Customer bulk operations
  updateCustomers: (data: BulkUpdateCustomerRequest) =>
    api.post<BulkOperationResult>('/bulk/customers/update', data),

  deleteCustomers: (data: BulkDeleteRequest) =>
    api.post<BulkOperationResult>('/bulk/customers/delete', data),

  // Product bulk operations
  updateProducts: (data: BulkUpdateProductRequest) =>
    api.post<BulkOperationResult>('/bulk/products/update', data),

  deleteProducts: (data: BulkDeleteRequest) =>
    api.post<BulkOperationResult>('/bulk/products/delete', data),

  // Order bulk operations
  updateOrderStatus: (data: BulkUpdateOrderStatusRequest) =>
    api.post<BulkOperationResult>('/bulk/orders/status', data),
  
  rollback: (data: { rollback_data: string; operation_type: string }) => 
    api.post<BulkOperationResult>('/bulk/rollback', data),
}; 

export interface DataQualityReport {
  overall_score: number;
  customer_quality: EntityQuality;
  product_quality: EntityQuality;
  order_quality: EntityQuality;
  issues: DataQualityIssue[];
  suggestions: CleaningSuggestion[];
  generated_at: string;
}

export interface EntityQuality {
  entity_type: string;
  total_records: number;
  score: number;
  completeness: number;
  uniqueness: number;
  validity: number;
  issues: QualityMetric[];
}

export interface QualityMetric {
  metric_name: string;
  issue_count: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  affected_records: string[];
}

export interface DataQualityIssue {
  id: string;
  entity_type: string;
  entity_id: string;
  issue_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  field_name?: string;
  current_value?: string;
  suggested_value?: string;
}

export interface CleaningSuggestion {
  id: string;
  title: string;
  description: string;
  affected_count: number;
  suggested_action: string;
  automation_possible: boolean;
  sql_query?: string;
}

export interface CleaningResult {
  suggestion_id: string;
  executed: boolean;
  affected_records: number;
  error_message?: string;
}

// Data Quality API
export const dataQualityApi = {
  getReport: () => api.get<DataQualityReport>('/data-quality/report'),
  executeCleaning: (suggestionId: string) => 
    api.post<CleaningResult>('/data-quality/clean', { suggestion_id: suggestionId }),
}; 

export interface ApiIntegration {
  id: string;
  name: string;
  integration_type: 'rest_api' | 'webhook' | 'scheduled_import' | 'realtime_sync';
  config: ApiConfig;
  is_active: boolean;
  last_sync?: string;
  created_at: string;
  updated_at: string;
}

export interface ApiConfig {
  base_url: string;
  auth_type: 'none' | 'api_key' | 'bearer_token' | 'basic_auth' | 'oauth2';
  auth_config: AuthConfig;
  endpoints: Record<string, EndpointConfig>;
  sync_schedule?: string;
  data_mappings: Record<string, DataMapping>;
}

export interface AuthConfig {
  api_key?: string;
  token?: string;
  username?: string;
  password?: string;
  client_id?: string;
  client_secret?: string;
  oauth_url?: string;
}

export interface EndpointConfig {
  path: string;
  method: string;
  headers: Record<string, string>;
  query_params: Record<string, string>;
  entity_type: string;
}

export interface DataMapping {
  source_field: string;
  target_field: string;
  transform?: string;
  required: boolean;
  default_value?: string;
}

export interface SyncResult {
  integration_id: string;
  started_at: string;
  completed_at?: string;
  status: 'running' | 'completed' | 'failed' | 'partial_success';
  records_processed: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  error_message?: string;
  details: SyncDetail[];
}

export interface SyncDetail {
  entity_type: string;
  entity_id?: string;
  action: string;
  error_message?: string;
}

export interface CreateIntegrationRequest {
  name: string;
  integration_type: 'rest_api' | 'webhook' | 'scheduled_import' | 'realtime_sync';
  config: ApiConfig;
}

// API Integration API
export const apiIntegrationApi = {
  getAll: () => api.get<ApiIntegration[]>('/integrations'),
  create: (data: CreateIntegrationRequest) => api.post<ApiIntegration>('/integrations', data),
  executeSync: (id: string) => api.post<SyncResult>(`/integrations/${id}/sync`),
  getSyncHistory: (id: string) => api.get<SyncResult[]>(`/integrations/${id}/history`),
  toggle: (id: string) => api.post<ApiIntegration>(`/integrations/${id}/toggle`),
}; 

// Add these new interfaces and API functions to your existing api.ts file

// Schema modification types
export interface CreateTableRequest {
  table_name: string;
  columns: CreateColumnRequest[];
  comment?: string;
}

export interface CreateColumnRequest {
  name: string;
  data_type: string;
  is_nullable: boolean;
  is_primary_key: boolean;
  default_value?: string;
  max_length?: number;
  comment?: string;
}

export interface ModifyTableRequest {
  table_name: string;
  operation: TableOperation;
}

export interface TableOperation {
  AddColumn?: CreateColumnRequest;
  DropColumn?: string;
  ModifyColumn?: ModifyColumnRequest;
  RenameTable?: string;
  AddComment?: string;
}

export interface ModifyColumnRequest {
  old_name: string;
  new_name?: string;
  data_type?: string;
  is_nullable?: boolean;
  default_value?: string;
  max_length?: number;
  comment?: string;
}

export interface CreateIndexRequest {
  index_name: string;
  table_name: string;
  columns: string[];
  is_unique: boolean;
  index_type?: string;
}

export interface CreateRelationshipRequest {
  constraint_name: string;
  source_table: string;
  source_column: string;
  target_table: string;
  target_column: string;
  on_delete?: string;
  on_update?: string;
}


// Database Management Types
export interface DatabaseInfo {
  name: string;
  owner: string;
  size?: string;
  encoding: string;
  collate: string;
  ctype: string;
  table_count: number;
  connection_count: number;
  created_at?: string;
}

export interface CreateDatabaseRequest {
  name: string;
  owner?: string;
  template?: string;
  encoding?: string;
  locale?: string;
  comment?: string;
}

export interface UpdateDatabaseRequest {
  name: string;
  new_name?: string;
  new_owner?: string;
  comment?: string;
}

export interface DatabaseStatisticsDetailed {
  name: string;
  size_bytes: number;
  size_pretty: string;
  table_count: number;
  connection_count: number;
  last_backup?: string;
  last_analyze?: string;
}

// Extend schema API to include database functions
export const schemaApi = {
  // Existing schema functions
  getSchema: () => api.get<DatabaseSchema>('/schema'),
  getAllTables: () => api.get<TableInfo[]>('/schema/tables'),
  getTableDetails: (tableName: string) => api.get<TableDetails>(`/schema/tables/${tableName}`),
  getTableColumns: (tableName: string) => api.get<ColumnInfo[]>(`/schema/tables/${tableName}/columns`),
  getRelationships: () => api.get<Relationship[]>('/schema/relationships'),
  getIndexes: () => api.get<IndexInfo[]>('/schema/indexes'),
  getStatistics: () => api.get<DatabaseStatistics>('/schema/statistics'),
  analyzeStatistics: () => api.post('/schema/statistics/analyze'),
  getSupportedDataTypes: () => api.get<string[]>('/schema/data-types'),

  // Add this new function:
  getAggregatedStatistics: () => 
    api.get<{
      databases: number;
      tables: number;
      relationships: number;
      indexes: number;
      columns: number;
    }>('/schema/aggregated-statistics'),

  // Database management functions
  getDatabases: () => api.get<DatabaseInfo[]>('/schema/databases'),
  getDatabaseInfo: (databaseName: string) => api.get<DatabaseInfo>(`/schema/databases/${databaseName}`),
  createDatabase: (data: CreateDatabaseRequest) => api.post('/schema/databases', data),
  deleteDatabase: (databaseName: string, force: boolean = false) => 
    api.delete(`/schema/databases/${databaseName}?force=${force}`),
  renameDatabase: (oldName: string, newName: string) => 
    api.put(`/schema/databases/${oldName}/rename`, { new_name: newName }),
  changeDatabaseOwner: (databaseName: string, newOwner: string) => 
    api.put(`/schema/databases/${databaseName}/owner`, { new_owner: newOwner }),
  getDatabaseStatistics: (databaseName: string) => 
    api.get<DatabaseStatisticsDetailed>(`/schema/databases/${databaseName}/statistics`),

  // Table management functions  
  createTable: (data: CreateTableRequest) => api.post('/schema/tables', data),
  dropTable: (tableName: string) => api.delete(`/schema/tables/${tableName}`),
  modifyTable: (data: ModifyTableRequest) => api.put('/schema/tables/modify', data),

  // Index management functions
  createIndex: (data: CreateIndexRequest) => api.post('/schema/indexes', data),
  dropIndex: (indexName: string) => api.delete(`/schema/indexes/${indexName}`),

  // Relationship management functions
  createRelationship: (data: CreateRelationshipRequest) => api.post('/schema/relationships', data),
  dropRelationship: (tableName: string, constraintName: string) => 
    api.delete(`/schema/relationships/${tableName}/${constraintName}`),

  // Database-scoped schema functions
  getSchemaForDatabase: (databaseName: string) => 
    api.get<DatabaseSchema>(`/schema?database=${databaseName}`),
  
  getTablesForDatabase: (databaseName: string) => 
    api.get<TableInfo[]>(`/schema/tables?database=${databaseName}`),
  
  getTableDetailsForDatabase: (databaseName: string, tableName: string) => 
    api.get<TableDetails>(`/schema/tables/${tableName}?database=${databaseName}`),
  
  getRelationshipsForDatabase: (databaseName: string, limit: number = 100, offset: number = 0) => 
    api.get<{relationships: Relationship[], pagination: PaginationInfo}>(`/schema/relationships?database=${databaseName}&limit=${limit}&offset=${offset}`),
  
  getIndexesForDatabase: (databaseName: string) => 
    api.get<IndexInfo[]>(`/schema/indexes?database=${databaseName}`),
  
  getStatisticsForDatabase: (databaseName: string) => 
    api.get<DatabaseStatistics>(`/schema/statistics?database=${databaseName}`),
};

// Schema modification API functions
export const schemaModificationApi = {
  // Table operations
  createTable: (data: CreateTableRequest, database?: string) => 
    api.post('/schema/tables' + (database ? `?database=${database}` : ''), data),
  
  dropTable: (tableName: string, database?: string) => 
    api.delete(`/schema/tables/${tableName}` + (database ? `?database=${database}` : '')),
  
  modifyTable: (data: ModifyTableRequest, database?: string) => 
    api.put('/schema/tables/modify' + (database ? `?database=${database}` : ''), data),
  
  // Index operations
  createIndex: (data: CreateIndexRequest, database?: string) => 
    api.post('/schema/indexes' + (database ? `?database=${database}` : ''), data),
  
  dropIndex: (indexName: string, database?: string) => 
    api.delete(`/schema/indexes/${indexName}` + (database ? `?database=${database}` : '')),
  
  // Relationship operations
  createRelationship: (data: CreateRelationshipRequest, database?: string) => 
    api.post('/schema/relationships' + (database ? `?database=${database}` : ''), data),
  
  dropRelationship: (tableName: string, constraintName: string, database?: string) => 
    api.delete(`/schema/relationships/${tableName}/${constraintName}` + (database ? `?database=${database}` : '')),
  
  // Utility
  getSupportedDataTypes: () => 
    api.get<string[]>('/schema/data-types'),
}; 

// Add these ETL Pipeline types and API functions to your existing api.ts file

// ETL Pipeline Types
export interface EtlPipeline {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  schedule?: string;
  config: PipelineConfig;
  created_by: string;
  created_at: string;
  updated_at: string;
  last_run?: string;
  next_run?: string;
}

export interface PipelineConfig {
  nodes: PipelineNode[];
  connections: PipelineConnection[];
  variables: Record<string, string>;
}

export interface PipelineNode {
  id: string;
  node_type: 'Source' | 'Transform' | 'Destination' | 'Filter' | 'Join' | 'Aggregate' | 'Script';
  name: string;
  position: { x: number; y: number };
  config: NodeConfig;
}

export interface NodeConfig {
  source_config?: SourceConfig;
  transform_config?: TransformConfig;
  destination_config?: DestinationConfig;
  filter_config?: FilterConfig;
  join_config?: JoinConfig;
  aggregate_config?: AggregateConfig;
  script_config?: ScriptConfig;
}

export interface SourceConfig {
  source_type: 'Database' | 'CsvFile' | 'JsonFile' | 'RestApi' | 'ExistingTable' | 'ExcelFile' | 'CloudStorage' | 'NeuronSemanticModel';
  
  // Database Config
  connection_string?: string;
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password?: string;
  ssl_enabled?: boolean;
  table_name?: string;
  query?: string;
  
  // File Config
  file_path?: string;
  has_headers?: boolean;

  
  // API Config
  api_endpoint?: string;
  http_method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  auth_type?: 'none' | 'basic' | 'bearer' | 'api_key';
  auth_config?: {
    username?: string;
    password?: string;
    token?: string;
    api_key?: string;
    key_header?: string;
  };
  
  // Cloud Storage Config
  object_path?: string;
  credentials?: Record<string, string>;
  
  // Neuron Semantic Model Config (NEW)
  neuron_semantic_model_config?: NeuronSemanticModelSourceConfig;
  
  // Data Preview & Schema
  preview_data?: any[];
  detected_schema?: Array<{
    column_name: string;
    data_type: string;
    nullable: boolean;
    sample_values: any[];
  }>;
  
  // Validation
  connection_valid?: boolean;
  last_validated?: string;

  // Add these missing properties:
  cloud_provider?: string;
  bucket_name?: string;
  access_key?: string;
  secret_key?: string;
  delimiter?: string;
  encoding?: string;
  sheet_name?: string;
  range?: string;
  container_name?: string; // Add this field
}

export interface TransformConfig {
  transformations: Transformation[];
}

export interface Transformation {
  field: string;
  operation: 'Rename' | 'Cast' | 'Concatenate' | 'Split' | 'Replace' | 'Uppercase' | 'Lowercase' | 'Trim' | 'AddConstant' | 'Calculate';
  value?: string;
  target_field?: string;
}

export interface DestinationConfig {
  destination_type: 'Database' | 'CsvFile' | 'JsonFile' | 'ExistingTable' | 'SemanticModel' | 'NeuronSemanticModel';
  table_name: string;
  write_mode: 'Insert' | 'Upsert' | 'Replace' | 'Append' | 'FullRefresh' | 'IncrementalAppend' | 'IncrementalMerge' | 'SCD2';
  connection_string?: string;
  file_path?: string;  // Add this field for file-based destinations
  // Database connection fields
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password?: string;
  ssl_enabled?: boolean;
  // Semantic model specific fields
  semantic_model_config?: {
    semantic_model_id: string;
    target_table_name: string;
    update_mode: 'FullRefresh' | 'IncrementalAppend' | 'IncrementalMerge' | 'SCD2';
    schema_validation: boolean;
    partition_columns: string[];
    enable_upsert: boolean;
    upsert_key_columns: string[];
  };
  // Neuron Semantic Model Config (NEW)
  neuron_semantic_model_config?: NeuronSemanticModelDestinationConfig;
}

export interface FilterConfig {
  groups: FilterGroup[];
  global_logic: 'AND' | 'OR';
  data_quality_rules: DataQualityRule[];
  custom_expressions?: CustomExpression[];
}

export interface FilterGroup {
  id: string;
  conditions: FilterCondition[];
  logic: 'AND' | 'OR';
  enabled: boolean;
  name?: string;
}

export interface FilterCondition {
  id: string;
  field: string;
  operator: string;
  value: any;
  data_type: string;
  enabled: boolean;
  description?: string;
}

export interface DataQualityRule {
  id: string;
  rule_type: string;
  field: string;
  threshold: number | null;
  enabled: boolean;
  parameters?: any;
}

export interface CustomExpression {
  id: string;
  expression: string;
  enabled: boolean;
  description?: string;
}

export interface JoinConfig {
  join_type: 'Inner' | 'Left' | 'Right' | 'Full';
  left_key: string;
  right_key: string;
  right_source: string;
}

export interface AggregateConfig {
  group_by: string[];
  aggregations: Aggregation[];
}

export interface Aggregation {
  field: string;
  operation: 'Count' | 'Sum' | 'Avg' | 'Min' | 'Max' | 'CountDistinct';
  alias: string;
}

export interface ScriptConfig {
  language: 'Sql' | 'Python' | 'JavaScript';
  script: string;
  input_schema: FieldSchema[];
  output_schema: FieldSchema[];
}

export interface FieldSchema {
  name: string;
  data_type: string;
  nullable: boolean;
}

export interface PipelineConnection {
  id: string;
  source_node_id: string;
  source_port: string;
  target_node_id: string;
  target_port: string;
}

export interface PipelineExecution {
  id: string;
  pipeline_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at: string;
  completed_at?: string;
  duration_ms?: number;
  records_processed?: number;
  records_output?: number;
  error_message?: string;
  logs?: any;
  triggered_by: 'manual' | 'scheduled' | 'api_call' | 'data_change';
  triggered_by_user?: string;
}

export interface CreatePipelineRequest {
  name: string;
  description?: string;
  config: PipelineConfig;
}

export interface UpdatePipelineRequest {
  name?: string;
  description?: string;
  config?: PipelineConfig;
  schedule?: string;
  is_active?: boolean;
}

export interface PreviewRequest {
  config: PipelineConfig;
  node_id: string;
  limit?: number;
}

// ETL Pipeline API
export const etlApi = {
  // Pipeline management
  getAllPipelines: () => api.get<EtlPipeline[]>('/etl/pipelines'),
  getPipelineById: (id: string) => api.get<EtlPipeline>(`/etl/pipelines/${id}`),
  createPipeline: (data: CreatePipelineRequest) => api.post<EtlPipeline>('/etl/pipelines', data),
  updatePipeline: (id: string, data: UpdatePipelineRequest) => api.put<EtlPipeline>(`/etl/pipelines/${id}`, data),
  deletePipeline: (id: string) => {
    console.log('🔴 API: Deleting pipeline with ID:', id);
    return api.delete(`/etl/pipelines/${id}`)
      .then(response => {
        console.log('🔴 API: Delete response:', response);
        return response;
      })
      .catch(error => {
        console.error('🔴 API: Delete error:', error);
        throw error;
      });
  },
  
  // Pipeline execution
  executePipeline: (id: string) => api.post<PipelineExecution>(`/etl/pipelines/${id}/execute`),
  getPipelineExecutions: (id: string, limit?: number) => 
    api.get<PipelineExecution[]>(`/etl/pipelines/${id}/executions${limit ? `?limit=${limit}` : ''}`),
  getAllExecutions: (status?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());
    return api.get<PipelineExecution[]>(`/etl/executions${params.toString() ? '?' + params : ''}`);
  },
  
  // Pipeline utilities
  validatePipeline: (config: PipelineConfig) => api.post('/etl/validate', config),
  previewData: (data: PreviewRequest) => api.post('/etl/preview', data),
  
  // Cancel pipeline execution
  cancelExecution: (executionId: string) => 
    api.post<PipelineExecution>(`/etl/executions/${executionId}/cancel`),

  // Get semantic model destination options
  getSemanticModelDestinationOptions: () =>
    api.get<SemanticModelDestinationOptions>('/etl/semantic-model-options'),
};

export interface SemanticModelOption {
  semantic_model_id: string;
  semantic_model_name: string;
  description?: string;
  workspace_id: string;
  tables: Array<{
    name: string;
    table_type: string;
    description?: string;
    partition_columns: string[];
    schema: Array<{
      name: string;
      data_type: string;
      is_nullable: boolean;
      is_key: boolean;
    }>;
  }>;
  update_modes: Array<{
    value: string;
    label: string;
    description: string;
  }>;
}

export interface SemanticModelDestinationOptions {
  semantic_models: SemanticModelOption[];
  total_count: number;
}

// Add these lineage types and API functions to your existing api.ts:

// Lineage Types
export interface DataAsset {
  id: string;
  name: string;
  asset_type: 'table' | 'column' | 'file' | 'api' | 'view' | 'pipeline' | 'dashboard';
  schema_name?: string;
  table_name?: string;
  column_name?: string;
  file_path?: string;
  api_endpoint?: string;
  description?: string;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export interface LineageEdge {
  id: string;
  source_asset_id: string;
  target_asset_id: string;
  pipeline_id?: string;
  transformation_type: 'directcopy' | 'filter' | 'aggregate' | 'join' | 'transform' | 'union' | 'script' | 'unknown';
  transformation_logic?: string;
  confidence_score: number;
  created_at: string;
  last_verified: string;
}

export interface LineageGraph {
  assets: DataAsset[];
  edges: LineageEdge[];
  metadata: LineageMetadata;
}

export interface LineageMetadata {
  generated_at: string;
  depth_limit: number;
  total_assets: number;
  total_edges: number;
  root_assets: string[];
}

export interface ImpactAnalysisResult {
  source_asset: DataAsset;
  downstream_assets: DataAsset[];
  affected_pipelines: string[];
  impact_score: number;
  depth_levels: DataAsset[][];
}

export type LineageDirection = 'Upstream' | 'Downstream' | 'Both';

export interface CreateAssetRequest {
  name: string;
  asset_type: 'table' | 'column' | 'file' | 'api' | 'view' | 'pipeline' | 'dashboard';
  schema_name?: string;
  table_name?: string;
  column_name?: string;
  file_path?: string;
  api_endpoint?: string;
  description?: string;
  metadata?: any;
}

export interface CreateLineageRequest {
  source_asset_id: string;
  target_asset_id: string;
  pipeline_id?: string;
  transformation_type: 'directcopy' | 'filter' | 'aggregate' | 'join' | 'transform' | 'union' | 'script' | 'unknown';
  transformation_logic?: string;
  confidence_score?: number;
}

export interface LineageStats {
  total_assets: number;
  total_edges: number;
  assets_by_type: { asset_type: string; count: number }[];
  generated_at: string;
}

// Lineage API
export const lineageApi = {
  // Assets
  getAllAssets: (params?: {
    asset_type?: string;
    schema_name?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.asset_type) searchParams.append('asset_type', params.asset_type);
    if (params?.schema_name) searchParams.append('schema_name', params.schema_name);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    
    return api.get<DataAsset[]>(`/lineage/assets${searchParams.toString() ? '?' + searchParams : ''}`);
  },

  createAsset: (data: CreateAssetRequest) => 
    api.post<DataAsset>('/lineage/assets', data),

  // Lineage graphs
  getLineageGraph: (assetId: string, params?: {
    direction?: LineageDirection;
    depth?: number;
    include_metadata?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.direction) searchParams.append('direction', params.direction);
    if (params?.depth) searchParams.append('depth', params.depth.toString());
    if (params?.include_metadata) searchParams.append('include_metadata', params.include_metadata.toString());
    
    return api.get<LineageGraph>(`/lineage/assets/${assetId}/lineage${searchParams.toString() ? '?' + searchParams : ''}`);
  },

  // Impact analysis
  getImpactAnalysis: (assetId: string) => 
    api.get<ImpactAnalysisResult>(`/lineage/assets/${assetId}/impact`),

  // Lineage relationships
  createLineage: (data: CreateLineageRequest) => 
    api.post<LineageEdge>('/lineage/edges', data),

  // Discovery and stats
  discoverLineage: () => 
    api.post('/lineage/discover'),

  getLineageStats: () => 
    api.get<LineageStats>('/lineage/stats'),
}; 

export interface PaginationInfo {
  limit: number;
  offset: number;
  total: number;
  has_more: boolean;
} 

// Pipeline Template Types
export interface PipelineTemplate {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  difficulty_level: DifficultyLevel;
  config: PipelineConfig;
  parameters: TemplateParameter[];
  tags: string[];
  icon?: string;
  usage_count: number;
  is_official: boolean;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  version: string;
}

export type TemplateCategory = 
  | 'dataingestion' 
  | 'datatransformation' 
  | 'datavalidation' 
  | 'dataexport' 
  | 'analytics' 
  | 'monitoring' 
  | 'integration' 
  | 'workflow';

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export interface TemplateParameter {
  name: string;
  display_name: string;
  description: string;
  parameter_type: ParameterType;
  default_value?: string;
  required: boolean;
  validation_rules?: ValidationRule[];
}

export type ParameterType = 
  | 'String' 
  | 'Integer' 
  | 'Float' 
  | 'Boolean' 
  | 'Select' 
  | 'MultiSelect' 
  | 'TableName' 
  | 'ColumnName' 
  | 'DatabaseConnection';

export interface ValidationRule {
  rule_type: ValidationType;
  value: string;
  message: string;
}

export type ValidationType = 
  | 'MinLength' 
  | 'MaxLength' 
  | 'Pattern' 
  | 'MinValue' 
  | 'MaxValue' 
  | 'OneOf';

export interface TemplateUsage {
  id: string;
  template_id: string;
  user_id: string;
  pipeline_id?: string;
  used_at: string;
  parameters_used: Record<string, string>;
  success: boolean;
  error_message?: string;
}

export interface CreateTemplateRequest {
  name: string;
  description: string;
  category: TemplateCategory;
  difficulty_level: DifficultyLevel;
  config: PipelineConfig;
  parameters: TemplateParameter[];
  tags: string[];
  icon?: string;
  is_official?: boolean;
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  category?: TemplateCategory;
  difficulty_level?: DifficultyLevel;
  config?: PipelineConfig;
  parameters?: TemplateParameter[];
  tags?: string[];
  icon?: string;
  is_active?: boolean;
}

export interface CreatePipelineFromTemplateRequest {
  pipeline_name: string;
  pipeline_description?: string;
  parameter_values: Record<string, string>;
}

// Template API
export const templateApi = {
  // Template management
  getAllTemplates: (params?: {
    category?: TemplateCategory;
    difficulty?: DifficultyLevel;
    official_only?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    if (params?.difficulty) searchParams.append('difficulty', params.difficulty);
    if (params?.official_only) searchParams.append('official_only', params.official_only.toString());
    
    return api.get<PipelineTemplate[]>(`/etl/templates${searchParams.toString() ? '?' + searchParams : ''}`);
  },

  getTemplateById: (id: string) => 
    api.get<PipelineTemplate>(`/etl/templates/${id}`),

  createTemplate: (data: CreateTemplateRequest) => 
    api.post<PipelineTemplate>('/etl/templates', data),

  updateTemplate: (id: string, data: UpdateTemplateRequest) => 
    api.put<PipelineTemplate>(`/etl/templates/${id}`, data),

  deleteTemplate: (id: string) => 
    api.delete(`/etl/templates/${id}`),

  searchTemplates: (params: {
    q?: string;
    category?: TemplateCategory;
    difficulty?: DifficultyLevel;
    tags?: string[];
  }) => {
    const searchParams = new URLSearchParams();
    if (params.q) searchParams.append('q', params.q);
    if (params.category) searchParams.append('category', params.category);
    if (params.difficulty) searchParams.append('difficulty', params.difficulty);
    if (params.tags) params.tags.forEach(tag => searchParams.append('tags', tag));
    
    return api.get<PipelineTemplate[]>(`/etl/templates/search?${searchParams}`);
  },

  // Template usage
  createPipelineFromTemplate: (templateId: string, data: CreatePipelineFromTemplateRequest) => 
    api.post<EtlPipeline>(`/etl/templates/${templateId}/use`, data),

  getTemplateUsageStats: (templateId: string) => 
    api.get<{
      total_uses: number;
      unique_users: number;
      successful_uses: number;
      failed_uses: number;
      success_rate: number;
    }>(`/etl/templates/${templateId}/stats`),
};

// Data Source Types
export interface ConnectionTestRequest {
  source_type: string;
  connection_config: any;
}

export interface ConnectionTestResponse {
  success: boolean;
  message: string;
  connection_time_ms?: number;
}

export interface DataPreviewRequest {
  source_type: string;
  connection_config: any;
  query_config?: any;
  limit?: number;
}

export interface DataPreviewResponse {
  columns: string[];
  rows: any[][];
  total_rows?: number;
  sample_size: number;
}

export interface SchemaColumn {
  column_name: string;
  data_type: string;
  nullable: boolean;
  sample_values: any[];
}

export interface SchemaDetectionResponse {
  columns: SchemaColumn[];
  estimated_rows?: number;
}

export interface SchemaDetectionRequest {
  connection_config: any;
  table_name?: string;
}

// Data Source API
export const dataSourceApi = {
  testConnection: (data: ConnectionTestRequest) => 
    api.post<ConnectionTestResponse>('/data-sources/test-connection', data),

  previewData: (data: DataPreviewRequest) => 
    api.post<DataPreviewResponse>('/data-sources/preview', data),

  detectSchema: (data: SchemaDetectionRequest) => 
    api.post<SchemaDetectionResponse>('/data-sources/schema', data),
};

// Add these types to the existing api.ts file:

export interface TransformTestRequest {
  transformations: any[];
  sample_data: any[];
  schema: any[];
}

export interface TransformTestResponse {
  success: boolean;
  transformed_data: any[];
  schema_changes: any[];
  errors: any[];
  performance_metrics: {
    execution_time_ms: number;
    rows_processed: number;
    rows_succeeded: number;
    rows_failed: number;
  };
}

// Add to existing API exports:
export const transformApi = {
  testTransformations: (data: TransformTestRequest) => 
    api.post<TransformTestResponse>('/etl/transform/test', data),
};

export const filterApi = {
  testFilters: (request: any) => 
    api.post('/etl/filter/test', request),
};

// Add these destination types and API at the end of the file:

// Destination API Types
export interface DestinationTestRequest {
  destination_type: string;
  connection_config: any;
  table_name: string;
  write_mode: string;
  sample_data?: any[];
}

export interface DestinationTestResponse {
  success: boolean;
  message: string;
  connection_time_ms?: number;
  schema_validation?: {
    table_exists: boolean;
    schema_match: boolean;
    missing_columns: string[];
    extra_columns: string[];
    suggested_schema: SchemaColumn[];
  };
  write_preview?: {
    estimated_rows: number;
    write_strategy: string;
    potential_conflicts: string[];
  };
}

export interface WriteDataRequest {
  destination_type: string;
  connection_config: any;
  table_name: string;
  write_mode: string;
  data: any[];
  schema: SchemaColumn[];
}

export interface WriteDataResponse {
  success: boolean;
  message: string;
  rows_written: number;
  execution_time_ms: number;
  errors: Array<{
    row_index: number;
    error_type: string;
    message: string;
  }>;
}

// Destination API
export const destinationApi = {
  testDestination: (data: DestinationTestRequest) => 
    api.post<DestinationTestResponse>('/etl/destination/test', data),

  writeData: (data: WriteDataRequest) => 
    api.post<WriteDataResponse>('/etl/destination/write', data),
};

// Semantic Model Types
export interface SemanticModel {
  id: string;
  name: string;
  description?: string;
  workspace_id: string;
  version: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  delta_workspace_path: string; // Add this missing field
  refresh_schedule?: string;
  last_refresh?: string;
  configuration: {
    tables: Record<string, TableDefinition>;
    dimensions: Record<string, DimensionDefinition>;
    measures: Record<string, MeasureDefinition>;
    relationships: Record<string, RelationshipDefinition>;
  };
}

export interface TableDefinition {
  name: string;
  table_type: 'Fact' | 'Dimension';
  delta_table_path: string;
  schema: ColumnSchema[];
  description?: string;
  partition_columns: string[];
  is_active: boolean;
}

export interface ColumnSchema {
  name: string;
  data_type: string;
  is_nullable: boolean;
  is_key: boolean;
  description?: string;
  format_string?: string;
  default_aggregation?: AggregationFunction;
}

export interface DimensionDefinition {
  name: string;
  display_name: string;
  table_name: string;
  column_name: string;
  data_type: string;
  hierarchy?: string[];
  description?: string;
  is_hidden: boolean;
  format_string?: string;
  sort_order?: SortOrder;
  default_member?: string;
}

export interface MeasureDefinition {
  name: string;
  display_name: string;
  expression: MeasureExpression;
  format_string?: string;
  description?: string;
  is_hidden: boolean;
  folder?: string;
  data_type: string;
}

export interface MeasureExpression {
  type: 'Simple' | 'Calculated' | 'TimeIntelligence';
  simple?: {
    table: string;
    column: string;
    aggregation: AggregationFunction;
  };
  calculated?: {
    expression: string;
    dependencies: string[];
  };
  time_intelligence?: {
    base_measure: string;
    time_function: TimeFunction;
    date_column: string;
  };
}

export interface RelationshipDefinition {
  name: string;
  from_table: string;
  from_column: string;
  to_table: string;
  to_column: string;
  cardinality: Cardinality;
  cross_filter_direction: CrossFilterDirection;
  is_active: boolean;
  description?: string;
}

export type AggregationFunction = 'Sum' | 'Count' | 'CountDistinct' | 'Average' | 'Min' | 'Max' | 'First' | 'Last';
export type TimeFunction = 'YearToDate' | 'QuarterToDate' | 'MonthToDate' | 'SamePeriodLastYear' | 'PreviousPeriod';
export type Cardinality = 'OneToOne' | 'OneToMany' | 'ManyToOne' | 'ManyToMany';
export type CrossFilterDirection = 'Both' | 'Single' | 'None';
export type SortOrder = 'Ascending' | 'Descending' | 'None';

export interface CreateSemanticModelRequest {
  name: string;
  description?: string;
  workspace_id: string;
  tables: TableDefinition[];
  dimensions: DimensionDefinition[];
  measures: MeasureDefinition[];
  relationships: RelationshipDefinition[];
}

export interface HierarchyType {
  id: string;
  name: string;
  description?: string;
}

export interface UpdateHierarchyRequest {
  hierarchy: HierarchyType;
}

// Query Interface Types
export interface DaxFilter {
  field: string;
  operator: 'Equals' | 'NotEquals' | 'GreaterThan' | 'LessThan' | 'GreaterThanOrEqual' | 'LessThanOrEqual' | 'In' | 'NotIn' | 'Contains' | 'StartsWith' | 'EndsWith' | 'IsNull' | 'IsNotNull' | 'Between';
  value: any;
  data_type: string;
}

export interface DaxOrderBy {
  column: string;
  direction: 'Ascending' | 'Descending';
}

export interface DaxQueryRequest {
  semantic_model_id: string;
  measures: string[];
  dimensions: string[];
  filters: DaxFilter[];
  order_by: DaxOrderBy[];
  limit?: number;
  cache_duration_seconds?: number;
}

export interface DaxQueryResult {
  columns: QueryColumn[];
  rows: any[][];
  total_rows: number;
  execution_time_ms: number;
  cache_hit: boolean;
  query_id: string;
  sql_query?: string;
  warnings: string[];
}

export interface QueryColumn {
  name: string;
  display_name: string;
  data_type: string;
  format_string?: string;
  is_measure: boolean;
}

export interface QueryHistory {
  id: string;
  semantic_model_id: string;
  user_id: string;
  measures: string[];
  dimensions: string[];
  filters: DaxFilter[];
  order_by: DaxOrderBy[];
  limit?: number;
  execution_time_ms: number;
  rows_returned: number;
  cache_hit: boolean;
  error_message?: string;
  executed_at: string;
  query_name?: string;
}

export interface SavedQuery {
  id: string;
  name: string;
  description?: string;
  semantic_model_id: string;
  user_id: string;
  query: DaxQueryRequest;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  last_executed?: string;
}

export interface QueryValidation {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  estimated_execution_time_ms?: number;
  estimated_rows?: number;
}

export interface ChartConfig {
  chart_type: 'bar' | 'line' | 'pie' | 'scatter' | 'area' | 'table';
  x_axis?: string;
  y_axis?: string[];
  color_by?: string;
  show_legend: boolean;
  show_grid: boolean;
  title?: string;
  subtitle?: string;
}

export interface Dashboard {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  charts: DashboardChart[];
  layout: DashboardLayout;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface DashboardChart {
  id: string;
  name: string;
  query: DaxQueryRequest;
  chart_config: ChartConfig;
  position: { x: number; y: number };
  size: { width: number; height: number };
}

export interface DashboardLayout {
  columns: number;
  row_height: number;
  margin: [number, number];
  container_padding: [number, number];
}

// Semantic Model API
export const semanticModelApi = {
  async getById(id: string) {
    return api.get(`/semantic-models/${id}`);
  },
  
  async list(params?: { workspace_id?: string; is_active?: boolean; limit?: number; offset?: number }) {
    return api.get('/semantic-models', { params });
  },

  async create(data: any) {
    return api.post('/semantic-models', data);
  },

  update: (id: string, data: Partial<CreateSemanticModelRequest>) => 
    api.put<SemanticModel>(`/semantic-models/${id}`, data),

  delete: (id: string) => api.delete(`/semantic-models/${id}`),

  // Model components
  getTables: (id: string) => api.get(`/semantic-models/${id}/tables`),
  getDimensions: (id: string) => api.get(`/semantic-models/${id}/dimensions`),
  getMeasures: (id: string) => api.get(`/semantic-models/${id}/measures`),
  getRelationships: (id: string) => api.get(`/semantic-models/${id}/relationships`),
  getMetadata: (id: string) => api.get(`/semantic-models/${id}/metadata`),

  async addDimensionTable(modelId: string, data: AddDimensionTableRequest) {
    return api.post(`/semantic-models/${modelId}/dimension-tables`, data);
  },
  
  async addFactTable(modelId: string, data: AddFactTableRequest) {
    return api.post(`/semantic-models/${modelId}/fact-tables`, data);
  },

  async addHierarchy(modelId: string, data: AddHierarchyRequest) {
    return api.post(`/semantic-models/${modelId}/hierarchies`, data);
  },

  // Add components
  addDimension: (id: string, dimension: DimensionDefinition) => 
    api.post(`/semantic-models/${id}/dimensions`, { dimension }),

  addMeasure: (id: string, measure: MeasureDefinition) => 
    api.post(`/semantic-models/${id}/measures`, { measure }),

  addRelationship: (id: string, relationship: RelationshipDefinition) => 
    api.post(`/semantic-models/${id}/relationships`, { relationship }),

  async executeQuery(queryData: DaxQueryRequest) {
    return api.post('/semantic-models/query', queryData);
  },

  async addAttributeToTable(modelId: string, tableName: string, attribute: DimensionAttribute): Promise<void> {
    await api.post(`/neuron/semantic-models/${modelId}/tables/${tableName}/attributes`, attribute);
  },

  async addMeasureToTable(modelId: string, tableId: string, measure: Measure): Promise<void> {
    await api.post(`/semantic-models/${modelId}/fact-tables/${tableId}/measures`, measure);
  },

  async updateTableGranularity(modelId: string, tableId: string, foreignKeys: ForeignKey[], grainDescription: string): Promise<void> {
    await api.put(`/semantic-models/${modelId}/fact-tables/${tableId}/granularity`, {
      foreign_keys: foreignKeys,
      grain_description: grainDescription
    });
  },

  async updateHierarchy(modelId: string, hierarchyId: string, data: UpdateHierarchyRequest) {
    return api.put(`/semantic-models/${modelId}/hierarchies/${hierarchyId}`, data);
  },

  async deleteModel(modelId: string): Promise<void> {
    await api.delete(`/semantic-models/${modelId}`);
  },

  async getTemplates() {
    return api.get('/semantic-models/templates');
  },

  async createFromTemplate(data: {
    template_id: string;
    model_name: string;
    workspace_id: string;
    parameters: Array<{ name: string; value: string; }>;
  }) {
    return api.post('/semantic-models/from-template', data);
  },

  // Update methods for individual components
  async updateDimensionTable(modelId: string, tableId: string, data: any) {
    return api.put(`/semantic-models/${modelId}/dimension-tables/${tableId}`, data);
  },

  async updateFactTable(modelId: string, tableId: string, data: any) {
    return api.put(`/semantic-models/${modelId}/fact-tables/${tableId}`, data);
  },

  async updateMeasure(modelId: string, tableId: string, measureId: string, data: any) {
    return api.put(`/semantic-models/${modelId}/fact-tables/${tableId}/measures/${measureId}`, data);
  },

  async updateAttribute(modelId: string, tableId: string, attributeName: string, data: any) {
    return api.put(`/semantic-models/${modelId}/dimension-tables/${tableId}/attributes/${attributeName}`, data);
  },
};

// Enhanced Query API Types (matching backend)
export interface EnhancedDaxQueryRequest {
  semantic_model_id: string;
  measures: string[];
  dimensions: string[];
  filters?: DaxFilter[];
  order_by?: DaxOrderBy[];
  limit?: number;
  cache_duration_seconds?: number;
  timeout_seconds?: number;
  enable_streaming?: boolean;
  streaming_config?: StreamingQueryConfig;
  include_analytics?: boolean;
}

export interface StreamingQueryConfig {
  chunk_size: number;
  max_memory_mb: number;
  enable_compression: boolean;
}

export interface TimeTravelQueryRequest {
  query: EnhancedDaxQueryRequest;
  timestamp?: string;
  version?: number;
}

export interface ComplexQueryRequest {
  semantic_model_id: string;
  table_name: string;
  select_columns?: string[];
  filter_predicate?: string;
  order_by?: string[];
  limit?: number;
  version?: number;
  timestamp?: string;
  use_streaming?: boolean;
}

export interface QueryValidationRequest {
  semantic_model_id: string;
  measures: string[];
  dimensions: string[];
  filters?: DaxFilter[];
}

export interface QueryValidationResponse {
  valid: boolean;
  errors: string[];
  warnings: string[];
  estimated_execution_time_ms?: number;
  estimated_rows?: number;
}

export interface EnhancedDaxQueryResult {
  result: DaxQueryResult;
  execution_metadata: QueryExecutionMetadata;
  table_statistics: TableStatsSummary[];
}

export interface QueryExecutionMetadata {
  query_type: string;
  tables_accessed: string[];
  version_info: TableVersionInfo[];
  optimization_hints: string[];
  resource_usage: ResourceUsage;
}

export interface ResourceUsage {
  memory_used_mb: number;
  io_operations: number;
  network_bytes: number;
}

export interface TableStatsSummary {
  table_name: string;
  row_count: number;
  file_count: number;
  size_bytes: number;
}

export interface TableVersionInfo {
  table_name: string;
  version: number;
  timestamp: string;
}

export interface QueryPerformanceStats {
  query_id: string;
  semantic_model_id: string;
  user_id: string;
  execution_time_ms: number;
  rows_returned: number;
  cache_hit: boolean;
  tables_accessed: string[];
  timestamp: string;
}

export interface SemanticModelSchema {
  measures: FieldMetadata[];
  dimensions: FieldMetadata[];
  hierarchies: HierarchyMetadata[];
  relationships: RelationshipMetadata[];
}

export interface FieldMetadata {
  name: string;
  display_name: string;
  table_name: string;
  data_type: string;
  format_string?: string;
  description?: string;
  folder?: string;
  is_hidden: boolean;
  is_calculated: boolean;
  aggregation_function?: string;
  dependencies?: string[];
}

export interface HierarchyMetadata {
  name: string;
  display_name: string;
  table_name: string;
  levels: HierarchyLevelMetadata[];
  is_balanced: boolean;
  description?: string;
}

export interface HierarchyLevelMetadata {
  name: string;
  display_name: string;
  ordinal: number;
  is_unique: boolean;
  description?: string;
}

export interface RelationshipMetadata {
  name: string;
  from_table: string;
  from_column: string;
  to_table: string;
  to_column: string;
  cardinality: string;
  cross_filter_direction: string;
  is_active: boolean;
  description?: string;
}

export interface SmartSuggestion {
  type: 'measure' | 'dimension' | 'filter' | 'time_intelligence';
  field_name: string;
  reason: string;
  confidence: number;
  related_fields: string[];
}

export interface QueryTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  semantic_model_id?: string;
  query: DaxQueryRequest;
  parameters: QueryParameter[];
  tags: string[];
  is_official: boolean;
  created_by: string;
  created_at: string;
  usage_count: number;
}

export interface QueryParameter {
  name: string;
  display_name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'select';
  default_value?: any;
  options?: string[];
  required: boolean;
  description?: string;
}

// Enhanced Query Execution API
export const queryApi = {
  // Execute enhanced DAX query
  executeEnhanced: (queryData: EnhancedDaxQueryRequest) => 
    api.post<EnhancedQueryExecutionResponse>('/query/execute', queryData),

  // Execute time travel query
  executeTimeTravel: (queryData: TimeTravelQueryRequest) => 
    api.post<EnhancedQueryExecutionResponse>('/query/execute/time-travel', queryData),

  // Execute complex query
  executeComplex: (queryData: ComplexQueryRequest) => 
    api.post<EnhancedQueryExecutionResponse>('/query/execute/complex', queryData),

  // Legacy execute method (for backward compatibility)
  executeDax: (query: DaxQueryRequest) => 
    api.post<DaxQueryResult>('/query/execute', query),

  // Query validation
  validate: (queryData: QueryValidationRequest) => 
    api.post<QueryValidationResponse>('/query/validate', queryData),

  // Get query schema for semantic model
  getSchema: (semanticModelId: string) => 
    api.get<SemanticModelSchema>(`/query/models/${semanticModelId}/schema`),

  // Get smart suggestions
  getSmartSuggestions: (semanticModelId: string, selectedFields: string[]) => 
    api.post<SmartSuggestion[]>(`/query/models/${semanticModelId}/suggestions`, {
      selected_fields: selectedFields
    }),

  // Query history
  getHistory: (params?: {
    limit?: number;
    offset?: number;
    semantic_model_id?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    if (params?.semantic_model_id) searchParams.append('semantic_model_id', params.semantic_model_id);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    
    return api.get<QueryHistory[]>(`/query/history${searchParams.toString() ? '?' + searchParams : ''}`);
  },

  // Saved queries
  saveQuery: (data: {
    name: string;
    description?: string;
    semantic_model_id: string;
    query: DaxQueryRequest;
  }) => api.post<SavedQuery>('/query/saved', data),

  getSavedQueries: (params?: {
    semantic_model_id?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.semantic_model_id) searchParams.append('semantic_model_id', params.semantic_model_id);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    
    return api.get<SavedQuery[]>(`/query/saved${searchParams.toString() ? '?' + searchParams : ''}`);
  },

  deleteSavedQuery: (id: string) => api.delete(`/query/saved/${id}`),

  // Query templates
  getTemplates: (category?: string) => 
    api.get<QueryTemplate[]>('/query/templates', {
      params: { category }
    }),

  createFromTemplate: (templateId: string, parameters: Record<string, any>) => 
    api.post<DaxQueryRequest>(`/query/templates/${templateId}/create`, {
      parameters
    }),

  // Performance monitoring
  getPerformanceStats: (queryId: string) => 
    api.get<QueryPerformanceStats>(`/query/performance/${queryId}`),

  getPerformanceTrends: (semanticModelId: string, timeRange: string) => 
    api.get<QueryPerformanceStats[]>(`/query/performance/trends/${semanticModelId}`, {
      params: { time_range: timeRange }
    }),

  // Query management
  cancelQuery: (queryId: string) => api.post(`/query/cancel/${queryId}`),

  getExecutionPlan: (queryData: DaxQueryRequest) => 
    api.post<any>('/query/execution-plan', queryData),

  // Export results
  exportResults: (queryId: string, format: 'csv' | 'excel' | 'json' | 'pdf') => 
    api.get(`/query/results/${queryId}/export`, {
      params: { format },
      responseType: 'blob'
    }),
};

// Dashboard and Widget Library API Functions
// ===========================================

// Widget Library Types
export interface LibraryItem {
  id: string;
  name: string;
  description?: string;
  widget_id: string;
  config: any;
  data_bindings: any;
  category: string;
  tags: string[];
  is_favorite: boolean;
  is_public: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  version: string;
}

export interface CreateLibraryItemRequest {
  name: string;
  description?: string;
  widget_id: string;
  config: any;
  data_bindings: any;
  category: string;
  tags: string[];
  is_favorite: boolean;
  is_public: boolean;
  created_by: string;
  version: string;
}

// Dashboard Types
export interface DashboardType {
  id: string;
  name: string;
  description?: string;
  category: string;
  widgets: any; // JSONB array of widget instances
  layout: any;  // Grid layout configuration
  created_at: string;
  updated_at: string;
  created_by: string;
  is_public: boolean;
  tags: string[];
  version: string;
}

export interface CreateDashboardRequest {
  name: string;
  description?: string;
  category: string;
  widgets: any;
  layout: any;
  is_public: boolean;
  tags: string[];
}

export interface UpdateDashboardRequest {
  name?: string;
  description?: string;
  category?: string;
  widgets?: any;
  layout?: any;
  is_public?: boolean;
  tags?: string[];
}

export interface DashboardSummary {
  id: string;
  name: string;
  description?: string;
  category: string;
  widget_count: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  is_public: boolean;
  tags: string[];
}

export interface DashboardFilter {
  category?: string;
  created_by?: string;
  is_public?: boolean;
  tags?: string[];
}

// Widget Library API
export const widgetLibraryApi = {
  // Get all library items
  getAll: () => api.get<LibraryItem[]>('/library/items'),
  
  // Get single library item
  getById: (id: string) => api.get<LibraryItem>(`/library/items/${id}`),
  
  // Create new library item
  create: (data: CreateLibraryItemRequest) => api.post<LibraryItem>('/library/items', data),
  
  // Update library item
  update: (id: string, data: CreateLibraryItemRequest) => api.put<LibraryItem>(`/library/items/${id}`, data),
  
  // Delete library item
  delete: (id: string) => api.delete(`/library/items/${id}`),
  
  // Record download/usage
  recordDownload: (id: string) => api.post(`/library/items/${id}/download`),
  
  // Search library items
  search: (params: {
    query?: string;
    category?: string;
    tags?: string[];
    is_public?: boolean;
    is_favorite?: boolean;
    created_by?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.append('query', params.query);
    if (params.category) searchParams.append('category', params.category);
    if (params.tags) params.tags.forEach(tag => searchParams.append('tags', tag));
    if (params.is_public !== undefined) searchParams.append('is_public', String(params.is_public));
    if (params.is_favorite !== undefined) searchParams.append('is_favorite', String(params.is_favorite));
    if (params.created_by) searchParams.append('created_by', params.created_by);
    if (params.limit) searchParams.append('limit', String(params.limit));
    if (params.offset) searchParams.append('offset', String(params.offset));
    
    return api.get<LibraryItem[]>(`/library/items/search?${searchParams}`);
  },
  
  // Toggle favorite status
  toggleFavorite: (id: string) => api.post(`/library/items/${id}/favorite`),
  
  // Get favorites
  getFavorites: () => api.get<LibraryItem[]>('/library/items/favorites'),
  
  // Get by category
  getByCategory: (category: string) => api.get<LibraryItem[]>(`/library/items/category/${category}`),
  
  // Get public items
  getPublic: () => api.get<LibraryItem[]>('/library/items/public'),
  
  // Get user's items
  getMyItems: () => api.get<LibraryItem[]>('/library/items/mine'),
};

// Dashboard API
export const dashboardApi = {
  // Get all dashboards
  getAll: (params?: DashboardFilter) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    if (params?.created_by) searchParams.append('created_by', params.created_by);
    if (params?.is_public !== undefined) searchParams.append('is_public', String(params.is_public));
    if (params?.tags) params.tags.forEach(tag => searchParams.append('tags', tag));
    
    return api.get<DashboardSummary[]>(`/dashboards${searchParams.toString() ? '?' + searchParams : ''}`);
  },
  
  // Get single dashboard
  getById: (id: string) => api.get<DashboardType>(`/dashboards/${id}`),
  
  // Create new dashboard
  create: (data: CreateDashboardRequest) => api.post<DashboardType>('/dashboards', data),
  
  // Update dashboard
  update: (id: string, data: UpdateDashboardRequest) => api.put<DashboardType>(`/dashboards/${id}`, data),
  
  // Delete dashboard
  delete: (id: string) => api.delete(`/dashboards/${id}`),
  
  // Duplicate dashboard
  duplicate: (id: string, newName: string) => api.post<DashboardType>(`/dashboards/${id}/duplicate`, { name: newName }),
  
  // Share dashboard
  share: (id: string, isPublic: boolean) => api.patch<DashboardType>(`/dashboards/${id}/share`, { is_public: isPublic }),
  
  // Get dashboard templates
  getTemplates: () => api.get<DashboardSummary[]>('/dashboards/templates'),
  
  // Create from template
  createFromTemplate: (templateId: string, data: { name: string; description?: string }) => 
    api.post<DashboardType>(`/dashboards/templates/${templateId}/create`, data),
  
  // Search dashboards
  search: (params: {
    query?: string;
    category?: string;
    tags?: string[];
    is_public?: boolean;
    created_by?: string;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.append('query', params.query);
    if (params.category) searchParams.append('category', params.category);
    if (params.tags) params.tags.forEach(tag => searchParams.append('tags', tag));
    if (params.is_public !== undefined) searchParams.append('is_public', String(params.is_public));
    if (params.created_by) searchParams.append('created_by', params.created_by);
    if (params.limit) searchParams.append('limit', String(params.limit));
    if (params.offset) searchParams.append('offset', String(params.offset));
    
    return api.get<DashboardSummary[]>(`/dashboards/search?${searchParams}`);
  },
  
  // Get public dashboards
  getPublic: () => api.get<DashboardSummary[]>('/dashboards/public'),
  
  // Get user's dashboards
  getMyDashboards: () => api.get<DashboardSummary[]>('/dashboards/mine'),
  
  // Export dashboard
  export: (id: string, format: 'json' | 'pdf' | 'png') => 
    api.get(`/dashboards/${id}/export?format=${format}`, { responseType: 'blob' }),
  
  // Import dashboard
  import: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<DashboardType>('/dashboards/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Widget Instance API (for managing widgets within dashboards)
export const widgetInstanceApi = {
  // Create widget instance
  create: (dashboardId: string, widgetData: any) => 
    api.post(`/dashboards/${dashboardId}/widgets`, widgetData),
  
  // Update widget instance
  update: (dashboardId: string, widgetId: string, widgetData: any) => 
    api.put(`/dashboards/${dashboardId}/widgets/${widgetId}`, widgetData),
  
  // Delete widget instance
  delete: (dashboardId: string, widgetId: string) => 
    api.delete(`/dashboards/${dashboardId}/widgets/${widgetId}`),
  
  // Get widget data
  getData: (dashboardId: string, widgetId: string, params?: any) => 
    api.get(`/dashboards/${dashboardId}/widgets/${widgetId}/data`, { params }),
  
  // Refresh widget data
  refreshData: (dashboardId: string, widgetId: string) => 
    api.post(`/dashboards/${dashboardId}/widgets/${widgetId}/refresh`),
  
  // Export widget
  export: (dashboardId: string, widgetId: string, format: 'json' | 'csv' | 'excel' | 'png') => 
    api.get(`/dashboards/${dashboardId}/widgets/${widgetId}/export?format=${format}`, { responseType: 'blob' }),
};

// Sample Data API (for development/demo purposes)
export const sampleDataApi = {
  // Create sample widgets
  createSampleWidgets: () => api.post('/sample-data/widgets'),
  
  // Create sample dashboards
  createSampleDashboards: () => api.post('/sample-data/dashboards'),
  
  // Reset sample data
  resetSampleData: () => api.post('/sample-data/reset'),
  
  // Get sample data status
  getSampleDataStatus: () => api.get('/sample-data/status'),
};

export interface EnhancedQueryExecutionResponse {
  success: boolean;
  query_id: string;
  result?: EnhancedDaxQueryResult;
  error?: string;
  execution_time_ms: number;
  timestamp: string;
  query_type: string;
}

// Lake Explorer API
export const lakeExplorerApi = {
  exploreWorkspace: async (modelId: string) => {
    const response = await api.get(`/delta-explorer/${modelId}`);
    return response.data;
  },

  getTableDetails: async (modelId: string, tableName: string) => {
    const response = await api.get(`/delta-explorer/${modelId}/tables/${tableName}`);
    return response.data;
  },

  previewTableData: async (modelId: string, tableName: string, limit?: number) => {
    const params = limit ? { limit } : {};
    const response = await api.get(`/delta-explorer/${modelId}/tables/${tableName}/preview`, { params });
    return response.data;
  },

  getTableVersionHistory: async (modelId: string, tableName: string) => {
    const response = await api.get(`/delta-explorer/${modelId}/tables/${tableName}/versions`);
    return response.data;
  },
};

// Add these Neuron ETL types and API at the end of the file:

// Neuron ETL Source/Destination Types
export interface NeuronSemanticModelSourceConfig {
  semantic_model_id: string;
  selected_tables?: string[];
  selected_measures?: string[];
  query_expression?: string;
  optimization_level?: 'Basic' | 'Standard' | 'Aggressive' | 'AppleSiliconOptimized';
  enable_caching?: boolean;
  cache_ttl_minutes?: number;
  timeout_seconds?: number;
  enable_apple_silicon?: boolean;
  batch_size?: number;
  preview_limit?: number;
}

export interface NeuronSemanticModelDestinationConfig {
  semantic_model_id: string;
  target_table_name: string;
  write_mode: 'FullRefresh' | 'IncrementalAppend' | 'IncrementalMerge' | 'SCD2';
  optimization_level?: 'Basic' | 'Standard' | 'Aggressive' | 'AppleSiliconOptimized';
  enable_apple_silicon?: boolean;
  enable_materialization?: boolean;
  enable_real_time_updates?: boolean;
  schema_validation?: boolean;
  partition_columns?: string[];
  enable_upsert?: boolean;
  upsert_key_columns?: string[];
  batch_size?: number;
  compression_enabled?: boolean;
  parallel_writes?: boolean;
  memory_pool_size_mb?: number;
}

export interface NeuronDataPreviewRequest {
  source_type: 'NeuronSemanticModel';
  source_config: NeuronSemanticModelSourceConfig;
  limit?: number;
}

export interface NeuronDestinationTestRequest {
  destination_type: 'NeuronSemanticModel';
  destination_config: NeuronSemanticModelDestinationConfig;
  sample_data?: any[];
}

export interface NeuronDestinationTestResponse {
  success: boolean;
  message: string;
  apple_silicon_optimized?: boolean;
  estimated_performance?: {
    write_speed_mb_per_sec: number;
    memory_efficiency: number;
    cache_hit_rate_estimate: number;
  };
  schema_compatibility?: {
    compatible_columns: number;
    warnings: string[];
  };
  error?: string;
}

// Neuron ETL API (Apple Silicon Optimized) - Combined
export const neuronEtlApi = {
  // Source operations
  testNeuronSource: (data: NeuronDataPreviewRequest) => 
    api.post<DataPreviewResponse>('/etl/source/neuron/test', data),

  previewNeuronSource: (data: NeuronDataPreviewRequest) => 
    api.post<DataPreviewResponse>('/etl/source/neuron/preview', data),

  // Destination operations  
  testNeuronDestination: (data: NeuronDestinationTestRequest) => 
    api.post<NeuronDestinationTestResponse>('/etl/destination/neuron/test', data),

  // Get Neuron semantic model options for ETL
  getNeuronSemanticModelOptions: () =>
    api.get<{
      models: Array<{
        id: string;
        name: string;
        description?: string;
        version: string;
        workspace_id: string;
        is_active: boolean;
        health_score: number;
        tables: Array<{
          name: string;
          table_type: string;
          row_count?: number;
          partition_columns: string[];
          schema: {
            columns: Array<{
              name: string;
              data_type: string;
              is_nullable: boolean;
              is_key: boolean;
            }>;
          };
        }>;
        measures: Array<{
          name: string;
          display_name?: string;
          description?: string;
          performance_tier: 'Fast' | 'Medium' | 'Slow';
        }>;
      }>;
      total_count: number;
    }>('/etl/neuron/semantic-model-options'),

  // Pipeline management
  getAllPipelines: (page = 1, pageSize = 20, filter?: string, sortBy?: string) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (filter) params.append('filter', filter);
    if (sortBy) params.append('sort_by', sortBy);
    return api.get(`/neuron/etl/pipelines?${params.toString()}`);
  },
  
  getPipelineById: (id: string) => 
    api.get(`/neuron/etl/pipelines/${id}`),
    
  createPipeline: (data: {
    name: string;
    description?: string;
    config: {
      base_config: {
        nodes: any[];
        connections: any[];
      };
      neuron_nodes?: any[];
      neuron_connections?: any[];
      expression_mappings?: Record<string, any>;
      materialization_settings?: any;
      real_time_config?: any;
    };
    optimization_config?: any;
  }) => api.post('/neuron/etl/pipelines', data),
  
  updatePipeline: (id: string, data: {
    name?: string;
    description?: string;
    config?: {
      base_config: {
        nodes: any[];
        connections: any[];
      };
      neuron_nodes?: any[];
      neuron_connections?: any[];
      expression_mappings?: Record<string, any>;
      materialization_settings?: any;
      real_time_config?: any;
    };
    optimization_config?: any;
  }) => api.put(`/neuron/etl/pipelines/${id}`, data),
  
  deletePipeline: (id: string) => 
    api.delete(`/neuron/etl/pipelines/${id}`),
     
  // Pipeline execution
  executePipeline: (id: string, data: {
    execution_mode: string;
    optimization_level: string;
  }) => api.post(`/neuron/etl/pipelines/${id}/execute`, data),
  
  // Get all executions (placeholder for now - would need backend implementation)
  getAllExecutions: () => Promise.resolve({ data: [] }),
  
  // Analytics and optimization
  getPipelineAnalytics: (id: string, timeRange?: any, metrics?: string[], granularity?: string) => {
    const params = new URLSearchParams();
    if (timeRange) params.append('time_range', JSON.stringify(timeRange));
    if (metrics) params.append('metrics', JSON.stringify(metrics));
    if (granularity) params.append('granularity', granularity);
    return api.get(`/neuron/etl/pipelines/${id}/analytics?${params.toString()}`);
  },
  
  optimizePipeline: (id: string, data: {
    optimization_targets: string[];
    constraints?: any;
  }) => api.post(`/neuron/etl/pipelines/${id}/optimize`, data),
  
  // Neuron Expression transformations
  executeExpressionTransform: (data: {
    source_config: any;
    neuron_expressions: any[];
    optimization_hints?: any;
  }) => api.post('/neuron/etl/transform/expression', data),
  
  // Cache management
  getCacheStats: () => api.get('/neuron/etl/cache/stats'),
  warmCache: (id: string, data: {
    cache_layers: string[];
    priority?: number;
  }) => api.post(`/neuron/etl/cache/${id}/warm`, data),
};

// ─────────────────────────────────────────────────────────────
// BOM Management Studio Types
// ─────────────────────────────────────────────────────────────

export interface BomVersion {
  bom_version_id: string;
  product_id: string;
  version_number: number;
  version_type: 'INITIAL' | 'ENGINEER_EDIT';
  workflow_state: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'ARCHIVED';
  parent_version_id: string | null;
  reason_for_change: string | null;
  review_notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  submitted_by: string | null;
  submitted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  is_used_for_scheduling: boolean;
  scheduled_at: string | null;
  scheduled_by: string | null;
  is_active: boolean;
}

export interface BomVersionEntry {
  entry_id: string;
  bom_version_id: string;
  parent_product_id: string;
  component_material_id: string | null;
  component_product_id: string | null;
  component_code: string | null;
  component_name: string | null;
  component_type: 'material' | 'product';
  bom_level: number;
  sequence_number: number;
  quantity_per: number;
  uom: string;
  scrap_factor: number;
  is_critical: boolean;
  effective_date: string;
  expiration_date: string | null;
  change_type: 'ADDED' | 'MODIFIED' | 'REMOVED' | 'UNCHANGED';
  original_entry_id: string | null;
  notes: string | null;
  standard_cost: number | null;
}

export interface BomVersionWithEntries {
  version: BomVersion;
  entries: BomVersionEntry[];
  product_code: string | null;
  product_name: string | null;
  total_entries: number;
  changed_entries: number;
}

export interface BomVersionSummary {
  bom_version_id: string;
  product_id: string;
  product_code: string | null;
  product_name: string | null;
  version_number: number;
  version_type: 'INITIAL' | 'ENGINEER_EDIT';
  workflow_state: 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'ARCHIVED';
  parent_version_id: string | null;
  reason_for_change: string | null;
  created_by: string;
  created_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  is_used_for_scheduling: boolean;
  entry_count: number;
}

export interface BomLineageEvent {
  event_id: string;
  bom_version_id: string;
  product_id: string;
  event_type: 'CREATED' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'ARCHIVED' | 'USED_FOR_SCHEDULING' | 'ROLLED_BACK' | 'RECALLED';
  performed_by: string;
  performed_at: string;
  from_state: string | null;
  to_state: string | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
}

export interface BomPdfImport {
  import_id: string;
  product_id: string | null;
  original_filename: string;
  parsed_data: ParsedBomComponent[] | null;
  import_status: 'PENDING' | 'PARSED' | 'REVIEWED' | 'IMPORTED' | 'FAILED';
  created_bom_version_id: string | null;
  uploaded_by: string;
  uploaded_at: string;
  error_message: string | null;
  raw_text_preview: string | null;
}

export interface ParsedBomComponent {
  component_code: string;
  component_name: string;
  quantity_per: number;
  uom: string;
  bom_level: number;
  sequence_number: number;
  is_critical: boolean;
  notes: string | null;
}

export interface BomVersionEntryInput {
  component_material_id?: string | null;
  component_product_id?: string | null;
  bom_level: number;
  sequence_number: number;
  quantity_per: number;
  uom: string;
  scrap_factor: number;
  is_critical: boolean;
  effective_date: string;
  expiration_date?: string | null;
  change_type?: string;
  original_entry_id?: string | null;
  notes?: string | null;
}

export interface BomDiffEntry {
  sequence_number: number;
  bom_level: number;
  component_code: string | null;
  component_name: string | null;
  component_type: string;
  base_quantity_per: number | null;
  base_uom: string | null;
  base_scrap_factor: number | null;
  base_is_critical: boolean | null;
  base_notes: string | null;
  compare_quantity_per: number | null;
  compare_uom: string | null;
  compare_scrap_factor: number | null;
  compare_is_critical: boolean | null;
  compare_notes: string | null;
  change_type: 'ADDED' | 'MODIFIED' | 'REMOVED' | 'UNCHANGED';
}

export interface BomDiffResponse {
  base_version: BomVersionSummary;
  compare_version: BomVersionSummary;
  diff_entries: BomDiffEntry[];
  added_count: number;
  modified_count: number;
  removed_count: number;
  unchanged_count: number;
}

export interface BomLineageResponse {
  product_id: string;
  product_code: string | null;
  product_name: string | null;
  versions: BomVersionSummary[];
  events: BomLineageEvent[];
  initial_versions: BomVersionSummary[];
  pending_review_count: number;
  approved_count: number;
}

// ─────────────────────────────────────────────────────────────
// BOM Management Studio API
// ─────────────────────────────────────────────────────────────

export const bomStudioApi = {
  // Versions
  listVersions: (params?: {
    product_id?: string;
    version_type?: string;
    workflow_state?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.product_id) qs.append('product_id', params.product_id);
    if (params?.version_type) qs.append('version_type', params.version_type);
    if (params?.workflow_state) qs.append('workflow_state', params.workflow_state);
    if (params?.limit != null) qs.append('limit', String(params.limit));
    if (params?.offset != null) qs.append('offset', String(params.offset));
    const query = qs.toString();
    return api.get<BomVersionSummary[]>(`/mrp/bom-studio/versions${query ? `?${query}` : ''}`);
  },

  createVersion: (data: {
    product_id: string;
    version_type: string;
    reason_for_change?: string;
    parent_version_id?: string;
    entries?: BomVersionEntryInput[];
  }) => api.post<BomVersionWithEntries>('/mrp/bom-studio/versions', data),

  getVersion: (id: string) =>
    api.get<BomVersionWithEntries>(`/mrp/bom-studio/versions/${id}`),

  saveEntries: (versionId: string, entries: BomVersionEntryInput[]) =>
    api.put<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/entries`, { entries }),

  forkVersion: (versionId: string, reason_for_change?: string) =>
    api.post<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/fork`, { reason_for_change }),

  submitForReview: (versionId: string, reason_for_change: string) =>
    api.post<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/submit`, { reason_for_change }),

  recallVersion: (versionId: string, reason?: string) =>
    api.post<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/recall`, { reason }),

  compareVersions: (versionId: string, baseVersionId?: string) => {
    const qs = baseVersionId ? `?base_version_id=${baseVersionId}` : '';
    return api.get<BomDiffResponse>(`/mrp/bom-studio/versions/${versionId}/compare${qs}`);
  },

  approveVersion: (versionId: string, review_notes?: string) =>
    api.post<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/approve`, { review_notes }),

  rejectVersion: (versionId: string, review_notes: string) =>
    api.post<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/reject`, { review_notes }),

  scheduleFromVersion: (versionId: string, data: {
    quantity: number;
    due_date: string;
    work_center_id?: string;
    machine_id?: string;
    notes?: string;
  }) => api.post(`/mrp/bom-studio/versions/${versionId}/schedule`, data),

  rollbackVersion: (versionId: string, reason_for_change: string) =>
    api.post<BomVersionWithEntries>(`/mrp/bom-studio/versions/${versionId}/rollback`, { reason_for_change }),

  // Lineage & Audit
  getProductLineage: (productId: string) =>
    api.get<BomLineageResponse>(`/mrp/bom-studio/products/${productId}/lineage`),

  getAuditLog: (params?: {
    product_id?: string;
    bom_version_id?: string;
    performed_by?: string;
    event_type?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.product_id) qs.append('product_id', params.product_id);
    if (params?.bom_version_id) qs.append('bom_version_id', params.bom_version_id);
    if (params?.performed_by) qs.append('performed_by', params.performed_by);
    if (params?.event_type) qs.append('event_type', params.event_type);
    if (params?.start_date) qs.append('start_date', params.start_date);
    if (params?.end_date) qs.append('end_date', params.end_date);
    if (params?.limit != null) qs.append('limit', String(params.limit));
    const query = qs.toString();
    return api.get<BomLineageEvent[]>(`/mrp/bom-studio/audit${query ? `?${query}` : ''}`);
  },

  // PDF Import
  importPdf: (file: File, productId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    const qs = productId ? `?product_id=${productId}` : '';
    return api.post<BomPdfImport>(`/mrp/bom-studio/import/pdf${qs}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  getImportStatus: (importId: string) =>
    api.get<BomPdfImport>(`/mrp/bom-studio/import/${importId}`),

  confirmImport: (importId: string, data: {
    product_id: string;
    reason_for_change?: string;
    entries: BomVersionEntryInput[];
  }) => api.post<BomVersionWithEntries>(`/mrp/bom-studio/import/${importId}/confirm`, data),
};